// 3rd-party
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Typography,
  IconButton,
  MenuItem,
} from "@mui/material";

// Components
import Spinner from "../../components/spinner/Spinner";

// Services
import { uploadToAzureBlob } from "../../services/StatelevelService";
import { fetchSasUrl, logout } from "../../services/IndiaService";
import { getStorageItemLocal } from "../../services/storageServices";

// Config
import { appConfig } from "../../config/appConfig";

// AssetComponents
import arrowback from "../../assets/arrow_back.svg";
import edit from "../../assets/edit.svg";

// Styles (Own)
import "../StateData/StatePDFFormDialog.scss";

type AdminEntry = {
  [x: string]: any;
  id: number;
  state: string;
  stateName: string;
  imagename: string;
  imageUrls: string | string[];
  isActive: boolean;
  isIconActive: boolean;
  status: string;
};
interface State {
  id: number;
  name: string;
}

interface StateImageFormDialogProps {
  open: boolean;
  onClose?: () => void;

  fetchData: () => void;
  resetForm: () => void;

  formData: AdminEntry;
  setFormData: React.Dispatch<React.SetStateAction<AdminEntry>>;
  editingIndex: any;
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>;
  setEditingIndex: React.Dispatch<React.SetStateAction<any>>;
  states: any;
  dataList?: any;
}

const StateImageFormDialog: React.FC<StateImageFormDialogProps> = ({
  open,
  onClose,
  fetchData,
  resetForm,
  formData,
  setFormData,
  editingIndex,
  setShowForm,
  setEditingIndex,
  states,
  dataList,
}) => {
  // useStates
  const [selectedPdf, setSelectedPdf] = useState<File[]>([]);
  const [isActive, setIsActive] = useState(false);
  const [isIconActive, setisIconActive] = useState(false);
  const [fileName, setfileName] = useState("No PDF");
  const [pdferror, setPdfError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [Imagepreview, setImagepreview] = useState<string>("No Image");

  // useNavigate
  const navigate = useNavigate();

  // Constants
  const MAX_FILE_SIZE_MB1 = 15;
  const ALLOWED_TYPE = ["image/jpeg", "image/jpg", "image/png"];
  useEffect(() => {
    setIsActive(formData.isActive);
    setisIconActive(formData.isIconActive);

    let temp: string[] = [];

    if (Array.isArray(formData.imageUrls)) {
      temp = formData.imageUrls.map((img) => img?.trim?.()).filter(Boolean);
    } else if (typeof formData.imageUrls === "string") {
      temp = formData.imageUrls
        .split(",")
        .map((img) => img.trim())
        .filter(Boolean);
    }

    if (temp.length > 0) {
      const extractedFileName = temp[0].split("/").pop()?.trim() ?? "";

      setfileName(extractedFileName);
      setImagepreview(temp[0]);
    } else {
      setfileName(""); // clear filename fallback
      setImagepreview("");
    }
    if (selectedPdf && selectedPdf.length > 0) {
      setImagepreview(URL.createObjectURL(selectedPdf[0]));
    }
  }, [formData]);

  const handleCancel = () => {
    onClose?.();
    setShowForm(false);
    setEditingIndex(null);
    resetForm();
    setShowForm(false);
    setPdfError(null);
    setIsActive(false);
    setisIconActive(false);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      //   [e.target.name]: e.target.value,
      imagename: e.target.value,
      lastUpdated: new Date().toISOString(),
    });
  };
  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "application/pdf",
  ) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      if (type === "application/pdf") {
        setSelectedPdf(filesArray);
      }
    }
  };

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];

    if (files.length === 0) {
      setPdfError("Please select a PDF file.");
      return;
    }

    const file = files[0]; // Assuming single file upload for PDF

    if (!ALLOWED_TYPE.includes(file.type)) {
      setPdfError("Only JPEG, JPG and PNG are allowed.");
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB1 * 1024 * 1024) {
      setPdfError("File size must be less than or equal to 10MB.");
      return;
    }

    setPdfError(null);
    setImagepreview(URL.createObjectURL(file));
    setSelectedPdf([file]);
    // handleFileChange(e, "application/pdf"); //file.name
    setfileName(file.name);
  };

  const uploadToBlob = async (file: File) => {
    let TokenUrl = await fetchSasUrl("file-uploads");
    let pathPrefix = "SateImage";
    let fileUrl = await uploadToAzureBlob(
      file,
      "file-uploads",
      TokenUrl,
      pathPrefix,
    );
    // return fileUrl.split("?")[0] + "/" + file.name;
    return fileUrl.split("?")[0];
  };
  const handleSave = async () => {
    if (!formData.state) {
      alert("state is required!");
      return;
    }
    if (!formData.imagename) {
      alert("PDF Name is required!");
      return;
    }
    const currentStateEntries = dataList.filter(
      (entry: any) =>
        entry.state === formData.state &&
        (editingIndex === null || entry.id !== formData.id),
    );

    if (currentStateEntries.length >= 5 && editingIndex === null) {
      alert("Cannot add more than 5 entries for the selected state.");
      return;
    }
    const categoryItems = dataList.filter(
      (item: any) => item.state === formData.state,
    );

    if (formData.isIconActive) {
      const alreadyActive = categoryItems.find(
        (item: any) => item.isIconActive === true && item.id !== formData.id,
      );

      if (alreadyActive) {
        alert("Only one icon can be active per state.");
        return;
      }
    }
    setLoading(true);
    const formDataToSend = new FormData();
    formDataToSend.append("id", editingIndex?.toString() ?? "0");
    formDataToSend.append("state", formData.state);
    formDataToSend.append("imagename", formData.imagename);
    formDataToSend.append("isActive", isActive.toString());
    formDataToSend.append("isIconActive", isIconActive.toString());

    formDataToSend.append("status", "Pending"); //Approved  Pending for Approval

    const username = getStorageItemLocal("username") ?? "";
    formDataToSend.append("uploadBy", username);
    // Append selectedImages

    try {
      if (selectedPdf.length > 0) {
        const uploadPromises = selectedPdf.map(async (pdf) => {
          const fileUrl = await uploadToBlob(pdf);
          if (fileUrl) formDataToSend.append("imageUrls", fileUrl);
        });
        await Promise.all(uploadPromises);
      } else if (Array.isArray(formData.imageUrls)) {
        for (const url of formData.imageUrls) {
          const cleanUrl = typeof url === "string" ? url.trim() : "";
          if (cleanUrl && cleanUrl !== "null") {
            formDataToSend.append("imageUrls", cleanUrl);
          }
        }
      } else if (typeof formData.imageUrls === "string" && formData.imageUrls.trim()) {
        const cleanUrl = formData.imageUrls.trim();
        if (cleanUrl !== "null") {
          formDataToSend.append("imageUrls", cleanUrl);
        }
      }

      const url =
        editingIndex !== null
          ? `${appConfig.serviceUrls.saveStateImageEntries}/${formData.id}`
          : `${appConfig.serviceUrls.saveStateImageEntries}/upload`;

      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(url, {
        method: editingIndex !== null ? "PUT" : "POST",
        body: formDataToSend,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 401) {
        logout(navigate);
        return;
      }
      if (!response.ok) throw new Error("Failed to save data");
      alert("Data saved successfully");
      fetchData();
      resetForm();
      const pdfInput = document.getElementById("pdfUpload") as HTMLInputElement | null;
      if (pdfInput) pdfInput.value = "";
      setSelectedPdf([]);
      setEditingIndex(null);
      setShowForm(false);
    } catch (error) {
      console.error("Error saving data:", error);
      alert("Failed to save data");
    } finally {
      setLoading(false);
      setShowForm(false);
    }
  };
  const handleIsActive = () => {
    setIsActive(!isActive);

    setFormData({ ...formData, isActive: !isActive });
  };
  const handleisIconActive = () => {
    setisIconActive(!isIconActive);

    setFormData({ ...formData, isIconActive: !isIconActive });
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullWidth
      maxWidth={false} // Disable default width restriction
      PaperProps={{
        sx: {
          width: "900px",
          height: "600px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#F7F7F7",
          padding: "4px 16px", // Optional: improves appearance
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center", // center the items vertically
            gap: 1, // adds spacing between icon and text
          }}
        >
          <IconButton
            color="primary"
            size="small"
            onClick={handleCancel}
            className="custom-icon-button"
          >
            <img src={arrowback} alt="visible" className="custom-icon" />
          </IconButton>
          <Typography className="custom-icon-text">
            {editingIndex !== null
              ? "Edit State Level Highlight"
              : "Add State Level Highlight"}
          </Typography>
        </Box>

        <Box></Box>
      </Box>

      <Box sx={{ marginLeft: "20px" }}>
        <Typography className="text-style">State</Typography>
        <Box className="pdf-name-wrapper">
          <TextField
            select
            placeholder="Select State"
            size="small"
            name="state"
            value={formData.state}
            onChange={(e) =>
              setFormData({ ...formData, state: e.target.value })
            }
            variant="standard"
            fullWidth
            InputProps={{ disableUnderline: true }}
          >
            {states.map((st: State) => (
              <MenuItem key={st.id} value={st.id}>
                {st.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
      {/* 2nd */}
      {loading ? (
        <>
          {" "}
          <Spinner></Spinner>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            background: "#FFF",
            padding: "20px 16px",
          }}
        >
          {/* LEFT PANEL */}
          <Box className="left-panel">
            <Box className="thumbnail-frame">
              {Imagepreview ? (
                <img
                  loading="lazy"
                  src={Imagepreview}
                  alt="New Thumbnail"
                  width={205}
                  height={205}
                  style={{ objectFit: "contain" }}
                />
              ) : (
                <Typography>No Image</Typography>
              )}
            </Box>

            <Button className="edit-thumbnail" component="label">
              <img
                src={edit}
                alt="edit"
                style={{
                  width: "20px",
                  height: "20px",
                  paddingRight: "5px",
                }}
              />

              <Typography className="edit-thumb-text">Edit Image</Typography>
              <input
                //   id="imageUpload"
                type="file"
                hidden
                accept="image/jpeg,image/png"
                onChange={handlePdfUpload}
              />
            </Button>
            {pdferror && <Alert severity="error">{pdferror}</Alert>}
            {/*  )} */}
          </Box>
          {/* right panel */}
          <Box>
            <Box>
              <Typography className="text-style">Image Name</Typography>
              <Box className="pdf-name-wrapper2">
                <TextField
                  placeholder="Enter Image Name"
                  size="small"
                  name="imagename" // match the key
                  value={formData.imagename} // match the key
                  onChange={
                    (e) =>
                      setFormData({ ...formData, imagename: e.target.value }) // same key
                  }
                  variant="standard"
                  fullWidth
                  InputProps={{ disableUnderline: true }}
                  inputProps={{
                    maxLength: 24,
                  }}
                />
                <Typography className="pdf-char-count">
                  {formData.imagename?.length || 0}/24
                </Typography>
              </Box>
            </Box>
            {/*  */}

            <Box>
              <Typography className="text-style">Active/Disable</Typography>

              <Box
                onClick={handleIsActive}
                sx={{ padding: "15px" }}
                className="status-wrapper"
              >
                <Box
                  className={`status-toggle1 ${
                    isActive ? "active" : "Disable"
                  }`}
                >
                  <Box className="status-circle" />
                </Box>

                <Typography
                  sx={{ width: "30px" }}
                  className="active-txt"
                  component="span"
                >
                  {isActive ? "Active" : "Disable"}
                </Typography>
              </Box>

              {/* set Icon  */}

              <Typography className="text-style">Set as Icon</Typography>
              <Box
                onClick={handleisIconActive}
                sx={{ padding: "15px" }}
                className="status-wrapper"
              >
                <Box
                  className={`status-toggle1 ${
                    isIconActive ? "active" : "Disable"
                  }`}
                >
                  <Box className="status-circle" />
                </Box>

                <Typography
                  sx={{ width: "30px" }}
                  className="active-txt"
                  component="span"
                >
                  {isIconActive ? "Active" : "Disable"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <DialogActions>
        <Button
          onClick={handleCancel}
          className="cancel-button"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          disabled={pdferror != null}
          onClick={handleSave}
          className="submit-button"
        >
          {editingIndex !== null ? "Update" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StateImageFormDialog;
